import { DataTable } from '@porsche-code/kado-ui';
import { createDataTableColumnHelper as createColumnHelper } from '@porsche-code/kado-ui';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { NAMESPACES } from '@/config/i18n';
import { usePersonInfoQuery } from '@/graphql';

export const Route = createFileRoute('/admin/person-info/$personId/settings')({
  component: Settings,
});

function Settings() {
  const { t } = useTranslation(NAMESPACES);
  const { personId } = useParams({
    from: '/admin/person-info/$personId/settings',
  });

  const { data: personInfo, isPending } = usePersonInfoQuery(
    { id: +personId },
    {
      select: (data) => data.person,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  );

  const settings = Object.entries(personInfo?.settings ?? {}).map(
    ([name, value]) => ({ name, value }),
  );

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<(typeof settings)[0]>();

    return [
      columnHelper.accessor('name', {
        cell: (data) => data.getValue(),
        header: () => t('common:name'),
      }),
      columnHelper.accessor('value', {
        cell: (data) => {
          const value = data.getValue();
          if (data.row.original.name === 'is24HourFormat') {
            if (typeof value === 'boolean') {
              return value ? t('common:yes') : t('common:no');
            }

            return '-';
          }
          return value ?? '-';
        },
        header: () => t('common:value'),
        enableColumnFilter: false,
        enableSorting: false,
      }),
    ];
  }, [t]);

  return (
    <DataTable
      isLoading={isPending}
      caption={t('common:settings')}
      data={settings}
      columns={columns}
      idAccessor="name"
      pagination={{
        position: 'bottom',
      }}
      i18n={{
        filterLabel: (columnName) =>
          t('common:iconLabel.filter', { columnName }),
        optionAll: t('common:all'),
        buttonReset: t('common:action.reset'),
        buttonFilter: t('common:action.filter'),
        actionSearch: t('common:action.search'),
        noData: t('common:noData'),
        minLabel: t('common:min'),
        maxLabel: t('common:max'),
      }}
    />
  );
}
